import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { toast } from 'vue3-toastify';

import type { IProfile } from '@/interfaces/agency/clients';
import type {
  IActivityLog,
  IActivityLogDetails,
  IActivityLogResponse,
  IAddress,
  IAdministrationSchedule,
  ICommonEntity,
  IConfirmationDialog,
  ICustomSeed,
  IJobType,
  IProfileType,
  ISnackBar,
  IUpdateBulkProfileArea,
  IUpdateBulkProfileStatus,
  IUpdateProfileStatus
} from '@/interfaces/agency/common';
import commonService from '@/services/agency/common';

export const useCommonStore = defineStore('commonStore', {
  state: () => ({
    rail: false,
    snackBar: {
      show: false,
      text: '',
      color: 'red',
      timeout: 3000
    } as ISnackBar,
    profileAddress: [] as IAddress[],
    isProfileAddressLoading: false as boolean,
    confirmationDialog: {
      isDialogVisible: false,
      title: '',
      icon: '',
      date: '',
      time: '',
      confirmationQuestion: '',
      onDialogConfirm: null as ((value?: unknown) => void) | null,
      onDialogCancel: null as ((value?: unknown) => void) | null,
      onDialogDismiss: null as ((value?: unknown) => void) | null,
      okText: 'Confirm',
      cancelText: 'Cancel',
      isTableVisible: false,
      isTableHeader: [],
      isTableData: [],
      isTableTitle: '',
      isShowCloseBtn: false
    } as IConfirmationDialog,
    isLoading: false,
    dateFilterTypes: [
      {
        name: 'Day',
        value: 'day'
      },
      {
        name: 'Weekly',
        value: 'weekly'
      },
      {
        name: 'Monthly',
        value: 'monthly'
      },
      {
        name: 'Custom',
        value: 'custom'
      }
    ],
    formats: [
      {
        title: 'DD-MM-YYYY',
        value: 'd-m-Y'
      },
      {
        title: 'MM-DD-YYYY',
        value: 'm-d-Y'
      },
      {
        title: 'YYYY-MM-DD',
        value: 'Y-m-d'
      }
    ],
    gender: [
      { title: 'Male', value: 'male' },
      { title: 'Female', value: 'female' },
      { title: 'Other', value: 'other' },
      { title: 'Prefer Not to Say', value: 'prefer_not_to_say' }
    ] as ICustomSeed[],
    workingFrequency: [
      { title: 'Monthly', value: 'monthly' },
      { title: 'Weekly', value: 'weekly' },
      { title: 'Daily', value: 'daily' }
    ] as ICustomSeed[],
    contractType: [
      { title: 'Full-time', value: 'full-time' },
      { title: 'Part-time', value: 'part-time' }
    ] as ICustomSeed[],
    communicationMode: [
      { title: 'In-Person', value: 'in-person' },
      { title: 'Phone Call', value: 'phone_call' },
      { title: 'Email', value: 'email' },
      { title: 'Messaging App', value: 'messaging_app' },
      { title: 'Online Portal', value: 'online_portal' },
      { title: 'Text Message', value: 'text_message' },
      { title: 'Video Call', value: 'video_call' },
      { title: 'Written Communication', value: 'written_communication' },
      { title: 'Assistive Technology', value: 'assistive_technology' },
      { title: 'Social Media', value: 'social_media' }
    ] as ICustomSeed[],
    profileType: {} as IProfileType,
    administrationSchedule: [] as IAdministrationSchedule[],
    jobTypes: [] as IJobType[],
    days: [] as ICommonEntity[],
    roles: [] as IProfileType[],
    travelModes: [
      { title: 'Walking', value: 'walk' },
      { title: 'Bike/Bicycle', value: 'bike_or_bicycle' },
      { title: 'Car', value: 'car' },
      { title: 'Public Transport', value: 'public_transport' }
    ] as ICustomSeed[],
    pronounsAs: [
      { title: 'He/Him', value: 'he/him' },
      { title: 'She/Her', value: 'she/her' },
      { title: 'They/Them', value: 'they/them' }
    ] as ICustomSeed[],
    sexualOrientation: [
      { title: 'Heterosexual / Straight', value: 'heterosexual_or_straight' },
      { title: 'Gay', value: 'gay' },
      { title: 'Lesbian', value: 'lesbian' },
      { title: 'Bisexual', value: 'bisexual' },
      { title: 'Pansexual', value: 'pansexual' },
      { title: 'Asexual', value: 'asexual' },
      { title: 'Queer', value: 'queer' },
      { title: 'Prefer not to say', value: 'prefer_not_to_say' }
    ] as ICustomSeed[],
    addressType: [
      { title: 'Residential', value: 'residential' },
      { title: 'Billing', value: 'billing' },
      { title: 'Mailing or Correspondence', value: 'mailing_or_correspondence' },
      { title: 'Service Location', value: 'service_location' },
      { title: 'Shipping', value: 'shipping' },
      { title: 'Temporary', value: 'temporary' },
      { title: 'Emergency', value: 'emergency' },
      { title: 'Work', value: 'work' },
      { title: 'Care Facility', value: 'care_facility' },
      { title: 'Legal', value: 'legal' }
    ] as ICustomSeed[],
    timeSlots: [] as string[],
    tabs: [
      { title: 'Active', value: true },
      { title: 'Inactive', value: false }
    ],
    invoiceCycles: [
      { title: 'Weekly', value: 'weekly' },
      { title: 'Fortnightly', value: 'fortnightly' },
      { title: 'Monthly', value: 'monthly' },
      { title: 'Four Weekly', value: 'four_weekly' }

      // { title: 'Custom', value: 'custom' }
    ],
    invoiceStyles: [
      { title: 'Weekly Summary', value: 'weekly_summary' },
      { title: 'Detailed Breakdown', value: 'detailed_breakdown' }
    ],
    scheduleDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    currency: [
      {
        currency: 'US Dollar',
        symbol: '$',
        code: 'USD'
      },
      {
        currency: 'Euro',
        symbol: '€',
        code: 'EUR'
      },
      {
        currency: 'British Pound Sterling',
        symbol: '£',
        code: 'GBP'
      },
      {
        currency: 'Japanese Yen',
        symbol: '¥',
        code: 'JPY'
      },
      {
        currency: 'Swiss Franc',
        symbol: 'CHF',
        code: 'CHF'
      },
      {
        currency: 'Canadian Dollar',
        symbol: '$',
        code: 'CAD'
      },
      {
        currency: 'Australian Dollar',
        symbol: '$',
        code: 'AUD'
      },
      {
        currency: 'New Zealand Dollar',
        symbol: '$',
        code: 'NZD'
      },
      {
        currency: 'Indian Rupee',
        symbol: '₹',
        code: 'INR'
      }
    ],
    serviceActionType: [
      {
        title: 'Checkbox',
        value: 'checkbox'
      },
      {
        title: 'Radio Button',
        value: 'radio_button'
      },
      {
        title: 'Dropdown Options',
        value: 'dropdown_options'
      },
      {
        title: 'Range',
        value: 'range'
      },
      {
        title: 'Notes',
        value: 'notes'
      },
      {
        title: 'Picture',
        value: 'image'
      },
      {
        title: 'Digital Signature',
        value: 'digital_signature'
      }
    ],
    fieldTypes: [
      { value: 'number', title: 'Number' },
      { value: 'date', title: 'Date' },
      { value: 'string', title: 'Text' },
      { value: 'options', title: 'Dropdown' },
      { value: 'radio', title: 'Radio' },
      { value: 'checkbox', title: 'Checkbox' },
      { value: 'static_text', title: 'Static Text' },
      { value: 'file', title: 'File Upload' }
    ],
    settingsMenu: [
      {
        title: 'General',
        to: 'general',
        icon: { icon: 'tabler-settings-plus' },
        children: [
          { title: 'Jobs', to: 'jobs', icon: { icon: 'tabler-briefcase' } },
          { title: 'Employment Types', to: 'employment-types', icon: { icon: 'tabler-user-search' } },
          { title: 'Status', to: 'status', icon: { icon: 'tabler-file-check' } },
          { title: 'Schedule Timings', to: 'schedule-timing', icon: { icon: 'tabler-calendar-check' } },
          { title: 'Contact Types & Relations', to: 'contacts', icon: { icon: 'tabler-address-book' } },
          { title: 'Medications', to: 'medication', icon: { icon: 'tabler-vaccine-bottle' } },
          { title: 'Personal Identity Markers', to: 'personal-info', icon: { icon: 'tabler-id' } },
          { title: 'Absence Types', to: 'absence', icon: { icon: 'tabler-user-x' } },
          { title: 'Daily Tasks', to: 'daily-tasks', icon: { icon: 'tabler-checklist' } },
          { title: 'Skills', to: 'skills', icon: { icon: 'tabler-user-shield' } },
          { title: 'Services', to: 'services', icon: { icon: 'tabler-briefcase-2' } },
          { title: 'Funding Providers', to: 'funding-providers', icon: { icon: 'tabler-building-bank' } },
          { title: 'Expense Categories', to: 'expense-categories', icon: { icon: 'tabler-briefcase-2' } },
          { title: 'Holidays', to: 'holidays', icon: { icon: 'tabler-calendar-check' } },
          { title: 'Additional Fields', to: 'additional-fields', icon: { icon: 'tabler-file-text' } }
        ]
      },
      {
        title: 'Location',
        to: 'location',
        icon: { icon: 'tabler-map-pin' },
        children: [
          { title: 'Areas & Rota Configs', to: 'area-rota', icon: { icon: 'tabler-map-2' } },
          { title: 'Geo Lock & Time Clock', to: 'geo-lock', icon: { icon: 'tabler-lock-pin' } }
        ]
      },

      // {
      //   title: 'Care Plan',
      //   to: 'care-plan',
      //   icon: { icon: 'tabler-checklist' },
      //   children: [
      //     { title: 'Care Plan Names', to: 'names', icon: { icon: 'tabler-briefcase' } },
      //     { title: 'Templates', to: 'templates', icon: { icon: 'tabler-briefcase' } },
      //   ],
      // },
      {
        title: 'Agency',
        to: 'agency',
        icon: { icon: 'tabler-user-star' },
        children: [
          { title: 'Agency Details', to: 'agency-details', icon: { icon: 'tabler-user-circle' } },
          { title: 'Date & Time, Currency', to: 'date-time-currency', icon: { icon: 'tabler-calendar-clock' } },
          { title: 'Notification Settings', to: 'notification', icon: { icon: 'tabler-bell-cog' } },
          { title: 'Additional Settings', to: 'additional', icon: { icon: 'tabler-id' } }
        ]
      },

      // {
      //   title: 'Risk Assessment',
      //   to: 'risk-assessment',
      //   icon: { icon: 'tabler-emergency-bed' },
      //   children: [
      //     { title: 'Names', to: 'names', icon: { icon: 'tabler-briefcase' } },
      //   ],
      // },
      {
        title: 'User Management',
        to: 'user-management',
        icon: { icon: 'tabler-users' },
        children: [
          { title: 'Role Permissions', to: 'role-permissions', icon: { icon: 'tabler-settings-star' } },
          { title: 'User Permissions', to: 'user-permissions', icon: { icon: 'tabler-user-shield' } }
        ]
      },
      {
        title: 'Templates',
        to: 'templates',
        icon: { icon: 'tabler-checklist' },
        children: [
          {
            title: 'Care Plan Categories',
            to: 'careplan-categories',
            icon: { icon: 'tabler-tag' },
            group: 'care-plans'
          },
          {
            title: 'Assessment Categories',
            to: 'Assessment-categories',
            icon: { icon: 'tabler-clipboard-heart' },
            group: 'care-plans'
          },
          {
            title: 'Assessment Templates',
            to: 'Assessment-templates',
            icon: { icon: 'tabler-briefcase' },
            group: 'care-plans'
          }

          // { title: 'Notifications', to: 'notifications', icon: { icon: 'tabler-bell' } }
        ]
      },
      {
        title: 'Finance',
        to: 'finance',
        icon: { icon: 'tabler-settings-dollar' },
        children: [
          { title: 'Charge Rate Cards', to: 'rate-cards', icon: { icon: 'tabler-tags' }, group: 'charge-rates' },
          {
            title: 'Funding Profiles',
            to: 'funding-profiles',
            icon: { icon: 'tabler-building-bank' },
            group: 'charge-rates'
          },
          { title: 'Pay Rate Cards', to: 'pay-rates', icon: { icon: 'tabler-tags' }, group: 'pay-rates' },
          { title: 'Travel Expenses', to: 'travel-expense', icon: { icon: 'tabler-map-pin' }, group: 'pay-rates' },
          {
            title: 'Settings',
            to: 'finance-settings',
            icon: { icon: 'tabler-settings-dollar' },
            group: 'finance-settings'
          }
        ]
      },
      {
        title: 'Documents',
        to: 'documents',
        icon: { icon: 'tabler-file-dots' }
      },
      {
        title: 'Integrations',
        to: 'integrations',
        icon: { icon: 'tabler-plug-connected' },
        children: [{ title: 'Docezy', to: 'docezy', icon: { icon: 'tabler-file-certificate' } }]
      }
    ],
    resourceTypeItems: [
      { title: 'Job', value: 'Job' },
      { title: 'Contact Type', value: 'ContactType' },
      { title: 'Contact Relation', value: 'ContactRelation' },
      { title: 'Medication', value: 'Medication' },
      { title: 'Medication Route', value: 'MedicationRoute' },
      { title: 'Title', value: 'Title' },
      { title: 'Language', value: 'Language' },
      { title: 'Religion', value: 'Religion' },
      { title: 'Ethnic Group', value: 'EthnicGroup' },
      { title: 'Marital Status', value: 'MaritalStatus' },
      { title: 'Absence Type', value: 'AbsenceType' },
      { title: 'Task', value: 'Task' },
      { title: 'Skill', value: 'Skill' },
      { title: 'Profile Custom Section', value: 'ProfileCustomSection' },
      { title: 'Client', value: 'Client' },
      { title: 'Carer', value: 'Carer' },
      { title: 'Care Plan Assessment', value: 'CarePlanAssessment' },
      { title: 'Client Health Info', value: 'ClientHealthInfo' },
      { title: 'Client Foresight Info', value: 'ClientForesightInfo' },
      { title: 'Client Financial Info', value: 'ClientFinancialInfo' },
      { title: 'Profile Accommodation', value: 'ProfileAccommodation' },
      { title: 'Profile Identification Marker', value: 'ProfileIdentificationMarker' },
      { title: 'Profile Address', value: 'ProfileAddress' },
      { title: 'Profile Custom Data', value: 'ProfileCustomData' },
      { title: 'Client Uploaded Care Plan', value: 'ClientUploadCarePlan' },
      { title: 'Client Task', value: 'ClientTask' },
      { title: 'Client Medication', value: 'ClientMedication' },
      { title: 'Visit', value: 'Appointment' },
      { title: 'Visit Publication', value: 'AppointmentPublication' },
      { title: 'Visit Medication', value: 'AppointmentMedication' },
      { title: 'Visit Task', value: 'AppointmentTask' },
      { title: 'Profile', value: 'Profile' }
    ],
    moduleItems: [
      { title: 'Auth', value: 'Auth' },
      { title: 'Settings', value: 'Settings' },
      { title: 'Clients', value: 'Clients' },
      { title: 'Carers', value: 'Carers' },
      { title: 'Profile', value: 'Profile' }
    ],
    activityOperationItems: [
      { title: 'Create', value: 'CREATE' },
      { title: 'Update', value: 'UPDATE' },
      { title: 'Delete', value: 'DELETE' },
      { title: 'Approve', value: 'APPROVE' },
      { title: 'Reject', value: 'REJECT' },
      { title: 'Publish', value: 'PUBLISH' },
      { title: 'Unpublish', value: 'UNPUBLISH' },
      { title: 'Upload', value: 'UPLOAD' },
      { title: 'Visit Assigned', value: 'APPOINTMENT_ASSIGNED' },
      { title: 'Visit Unassigned', value: 'APPOINTMENT_UNASSIGNED' },
      { title: 'Reset', value: 'RESET' }
    ],
    activityLogResponse: {} as IActivityLogResponse,
    isActivityLogLoading: false as boolean,
    isCarerActivityLogLoading: false as boolean,
    appointmentActivityLogResponse: {} as IActivityLogResponse,
    activityLogDetails: {} as IActivityLogDetails,
    activityLogQuery: {
      page: 1,
      limit: 20,
      resourceId: null as number | null,
      resourceType: null as string | null,
      module: null as string | null,
      operation: null as string | null,
      role: null as string | null,
      resourceProfileId: null as number | null,
      relatedResourceId: null as number | null,
      relatedResourceType: null as string | null,
      startDate: null as string | null,
      endDate: null as string | null,
      environment: null as string | null
    },
    appointmentActivityLogQuery: {
      relatedResourceId: null as number | null,
      relatedResourceType: null as string | null,
      environment: null as string | null
    }
  }),
  getters: {
    getSnackBar(state): ISnackBar {
      return state.snackBar;
    },
    getSettingsMenu(state) {
      return state.settingsMenu;
    },

    // getFieldName:
    //   (state: {
    //     areas: IArea[];
    //     languages: ILanguage[];
    //     maritalStatuses: IMaritalStatus[];
    //     relation: IRelation[];
    //     ethnicGroups: IEthnicGroup[];
    //     religions: IReligion[];
    //   }) =>
    //   (name: string, id: number) => {
    //     switch (name) {
    //       case 'area': {
    //         const area = state.areas.find(item => item.id === id);

    //         return area ? area.name : '--';
    //       }

    //       case 'language': {
    //         const language = state.languages.find(item => item.id === id);

    //         return language ? language.name : '--';
    //       }

    //       case 'martial': {
    //         const martial = state.maritalStatuses.find(item => item.id === id);

    //         return martial ? martial.name : '--';
    //       }

    //       case 'relation': {
    //         const relation = state.relation.find(item => item.id === id);

    //         return relation ? relation.name : '--';
    //       }

    //       case 'ethnic': {
    //         const ethnic = state.ethnicGroups.find(item => item.id === id);

    //         return ethnic ? ethnic.name : '--';
    //       }

    //       case 'religion': {
    //         const religion = state.religions.find(item => item.id === id);

    //         return religion ? religion.name : '--';
    //       }
    //     }
    //   },
    getGender: state => state.gender,
    getProfileType: state => state.profileType,
    getJobTypes: state => state.jobTypes,
    getDays: state => state.days,
    getRoles: state => state.roles,
    getTravelModes: state => state.travelModes,
    getWorkingFrequency: state => state.workingFrequency,
    getContractType: state => state.contractType,
    getCommunicationMode: state => state.communicationMode,
    getPronounsAs: state => state.pronounsAs,
    getSexualOrientation: state => state.sexualOrientation,
    getAddressType: state => state.addressType,
    getCurrency: state => state.currency,
    getScheduleDays: state => state.scheduleDays,
    getTabs: state => state.tabs,
    getServiceActionType: state => state.serviceActionType,
    getFieldTypes: state => state.fieldTypes,
    getDateFilterTypes: state => state.dateFilterTypes,
    getDateFormat: state => {
      const storedFormat = localStorage.getItem('dateFormat') || 'dd-MM-yyyy';

      const matchedFormat = state.formats.find(f => f.title === storedFormat)?.value;
      const luxonFormat = matchedFormat || storedFormat;

      return luxonFormat.replace(/yyyy/g, 'Y').replace(/yy/g, 'y').replace(/MM/g, 'm').replace(/dd/g, 'd');
    },
    getTimeFormat: state => {
      const timeFormatSetting = JSON.parse(localStorage.getItem('timeFormat') || '{}');

      return timeFormatSetting?.h24 ? 'HH:mm' : 'hh:mm A';
    },
    getTimeSlots: state => () => {
      state.timeSlots = [];

      const stored = JSON.parse(localStorage.getItem('timeFormat') || '{}');
      const h24 = stored?.h24 ?? true;
      const timeInterval = stored?.timeInterval ?? 15;
      const timeFormat = h24 ? 'HH:mm' : 'hh:mm a';

      let startTime = DateTime.now().startOf('day');
      const endTime = DateTime.now().endOf('day');

      while (startTime < endTime) {
        state.timeSlots.push(startTime.toFormat(timeFormat));
        startTime = startTime.plus({ minutes: timeInterval });
      }

      return state.timeSlots;
    },

    getActivityLog: state => {
      return state.activityLogResponse.data as IActivityLog[];
    },

    getAppointmentActivityLog: state => {
      return state.appointmentActivityLogResponse.data as IActivityLog[];
    },

    getActivityLogDetails: state => {
      return state.activityLogDetails as IActivityLogDetails;
    },
    getResourceTypeItems: state => {
      return state.resourceTypeItems;
    },
    getModuleItems: state => {
      return state.moduleItems;
    },
    getActivityOperationItems: state => {
      return state.activityOperationItems;
    },
    getInvoiceCycles: state => {
      return state.invoiceCycles;
    },
    getInvoiceStyles: state => {
      return state.invoiceStyles;
    }
  },
  actions: {
    setSnackBar(show: boolean, text: string, color: string, timeout?: number) {
      this.snackBar = {
        show,
        text,
        color,
        timeout
      };
    },
    setLoading(isLoading: boolean) {
      this.isLoading = isLoading;
    },
    openDialog(
      options: IConfirmationDialog,
      onConfirm: (value?: unknown) => void,
      onCancel: (value?: unknown) => void,
      okText?: string,
      cancelText?: string
    ) {
      this.confirmationDialog.isDialogVisible = true;
      this.confirmationDialog.confirmationQuestion = options.confirmationQuestion;
      this.confirmationDialog.title = options.title;
      this.confirmationDialog.icon = options.icon;
      this.confirmationDialog.date = options.date;
      this.confirmationDialog.time = options.time;
      this.confirmationDialog.onDialogConfirm = onConfirm;
      this.confirmationDialog.onDialogCancel = onCancel;
      this.confirmationDialog.okText = okText || 'Confirm';
      this.confirmationDialog.cancelText = cancelText || 'Cancel';
      this.confirmationDialog.isTableVisible = options.isTableVisible || false;
      this.confirmationDialog.isTableData = options.isTableData;
      this.confirmationDialog.isTableHeader = options.isTableHeader;
      this.confirmationDialog.isTableTitle = options.isTableTitle;
      this.confirmationDialog.isShowCloseBtn = true;

      // Cleared so a previous dialog's dismiss handler can't fire for this one
      this.confirmationDialog.onDialogDismiss = null;
    },
    confirmDialog() {
      this.confirmationDialog.onDialogConfirm && this.confirmationDialog.onDialogConfirm();
      this.confirmationDialog.isDialogVisible = false;
    },
    cancelDialog() {
      this.confirmationDialog.onDialogCancel && this.confirmationDialog.onDialogCancel();
      this.confirmationDialog.isDialogVisible = false;
    },
    async deleteConfirmation(
      message: string,
      icon: string = 'delete',
      okText?: string,
      cancelText?: string,
      isShowCloseBtn?: boolean
    ) {
      await new Promise<void>((resolve, reject) => {
        const onCancel = () => reject(new Error('User cancelled'));

        this.openDialog(
          {
            isDialogVisible: true,
            confirmationQuestion: message,
            icon,
            isShowCloseBtn
          },
          () => resolve(),
          onCancel,
          okText,
          cancelText
        );

        // Closing via ✕ is treated the same as Cancel
        this.confirmationDialog.onDialogDismiss = onCancel;
      });
    },

    async confirmCancelDialog(
      message: string,
      icon: string,
      okText?: string,
      cancelText?: string,
      isShowCloseBtn?: boolean
    ) {
      await new Promise<void>((resolve, reject) => {
        this.openDialog(
          {
            isDialogVisible: true,
            confirmationQuestion: message,
            icon,
            isShowCloseBtn
          },
          () => resolve(),
          () => reject(new Error('User cancelled')),
          okText,
          cancelText
        );
      });
    },

    async confirmDismissDialog(
      message: string,
      icon: string,
      okText?: string,
      cancelText?: string,
      isShowCloseBtn?: boolean
    ) {
      return await new Promise<'confirm' | 'dismiss' | 'cancel'>(resolve => {
        this.openDialog(
          {
            isDialogVisible: true,
            confirmationQuestion: message,
            icon,
            isShowCloseBtn
          },
          () => resolve('confirm'),
          () => resolve('cancel'),
          okText,
          cancelText
        );

        this.confirmationDialog.onDialogDismiss = () => resolve('dismiss');
      });
    },

    dismissDialog() {
      this.confirmationDialog.onDialogDismiss && this.confirmationDialog.onDialogDismiss();
      this.confirmationDialog.isDialogVisible = false;
    },

    async uploadFile(profileId: number, file: File) {
      const data = await commonService.uploadFile(profileId, file);

      toast.success('Profile image updated successfully!');

      return data;
    },

    // Profile Address
    async fetchProfileAddress(profileId?: number, hideLoader = false) {
      this.isProfileAddressLoading = true;

      try {
        const { id } = JSON.parse(localStorage.getItem('userData') || '{}');

        const profileID = profileId || id;

        const { data } = await commonService.fetchProfileAddress(profileID, hideLoader);

        this.profileAddress = data;
      } finally {
        this.isProfileAddressLoading = false;
      }
    },

    async addProfileAddress(profileId: number, payload: IAddress) {
      await commonService.addProfileAddress(profileId, payload);
      this.fetchProfileAddress(profileId);

      toast.success('Address created successfully!');
    },

    async updateProfileAddress(profileId: number, payload: IAddress) {
      await commonService.updateProfileAddress(payload);

      this.fetchProfileAddress(profileId);
      toast.success('Address updated successfully!');
    },

    async updateProfile(profileId: number, payload: Partial<IProfile>) {
      await commonService.updateProfile(profileId, payload);

      toast.success('Profile updated successfully!');
    },

    async updateProfileStatus(profileId: number, payload: IUpdateProfileStatus) {
      await commonService.updateProfileStatus(profileId, payload);

      toast.success('Profile Status updated successfully!');
    },

    async updateBulkProfileStatus(payload: IUpdateBulkProfileStatus) {
      await commonService.updateBulkProfileStatus(payload);

      toast.success('Profile Status updated successfully!');
    },

    async updateBulkProfileArea(payload: IUpdateBulkProfileArea) {
      const { data } = await commonService.updateBulkProfileArea(payload);

      if (data.totalSucceeded > 0) {
        toast.success('Profile Area updated successfully!');
      }

      if (data.failed?.length > 0) {
        data.failed.forEach(item => {
          toast.error(`${item.reason}`);
        });
      }
    },

    async fetchDays() {
      const { data } = await commonService.getDays();

      this.days = data;
    },
    async fetchRoles() {
      const { data } = await commonService.getRoles();

      this.roles = data;
    },

    // todo
    async fetchProfileType(type: string) {
      const { data } = await commonService.profileType(type);

      this.profileType = data;
    },
    async fetchAdministrationSchedule() {
      const { data } = await commonService.getAdministrationSchedule();

      this.administrationSchedule = data;
    },
    async fetchJobTypes(status: boolean) {
      const { data } = await commonService.getJobTypes(status);

      this.jobTypes = data;
    },

    // Bulk Resend Login
    async bulkResendLogin(payload: { profileIds: number[] }) {
      await commonService.bulkResendLogin(payload);

      toast.success(payload.profileIds.length > 1 ? 'Bulk login resent successfully!' : 'Login resent successfully!');
    },

    // Activity Log
    async fetchActivityLog(append = false, hideLoader = false) {
      if (!append) {
        this.isActivityLogLoading = true;
      }

      try {
        const { data } = await commonService.fetchActivityLog(this.activityLogQuery, hideLoader);

        if (append) {
          this.activityLogResponse.data = [...this.activityLogResponse.data, ...data.data];
        } else {
          this.activityLogResponse.data = data.data;
        }
        if (append) {
          this.activityLogResponse.data = [...this.activityLogResponse.data, ...data.data];
        } else {
          this.activityLogResponse.data = data.data;
        }

        this.activityLogResponse.pagination = data.pagination;
      } finally {
        if (!append) {
          this.isActivityLogLoading = false;
        }
      }
    },

    async fetchAppointmentActivityLog(append = false) {
      const { data } = await commonService.fetchActivityLog(this.appointmentActivityLogQuery);

      this.appointmentActivityLogResponse = data;
    },

    async fetchClientActivityLog(append = false) {
      // Only the first page swaps the list for a skeleton; appending keeps the rows on screen.
      this.isActivityLogLoading = !append;
      try {
        const { data } = await commonService.fetchClientActivityLog(this.activityLogQuery);

        if (append) {
          this.activityLogResponse.data = [...this.activityLogResponse.data, ...data.data];
        } else {
          this.activityLogResponse.data = data.data;
        }

        this.activityLogResponse.pagination = data.pagination;
      } finally {
        this.isActivityLogLoading = false;
      }
    },

    async fetchCarerActivityLog(append = false) {
      if (!append) {
        this.isCarerActivityLogLoading = true;
      }

      try {
        const { data } = await commonService.fetchCarerActivityLog(this.activityLogQuery, true);

        if (append) {
          this.activityLogResponse.data = [...this.activityLogResponse.data, ...data.data];
        } else {
          this.activityLogResponse.data = data.data;
        }

        this.activityLogResponse.pagination = data.pagination;
      } finally {
        if (!append) {
          this.isCarerActivityLogLoading = false;
        }
      }
    },

    async fetchActivityLogById(id: number) {
      const { data } = await commonService.fetchActivityLogById(id);

      this.activityLogDetails = data;
    }
  }
});
